import * as piniaModels from './database';
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useAuthenticationStore } from './modules/authentication';
import { useCommonStore } from '../../shared/state/common';
import { useApplicationsStore } from './modules/applications';
import { useDashboardStore } from './modules/dashboard';
import { useInterviewStore } from './modules/interviews';
import { useJobsStore } from './modules/jobs';
import { useUsersStore } from './modules/users';

export const useMainStore = defineStore('main', {
    state: () => ({
        ...initialState(),
        dateFormat: 'LL',
        datetimeFormat: 'LLL',
        timeFormat: 'LT',
        authenticationStore: useAuthenticationStore(),
        commonStore: useCommonStore(),
    }),
    getters: {
        currentUser(state) {
            return state.authenticationStore.currentUser;
        },
        currentCompany(state) {
            return state.authenticationStore.currentCompany;
        },
        currentCompanyUser() {
            return this.currentUser.companyUsers.find(i => i.company_id === this.currentCompany.id);
        },
        hasModals(state) {
            return state.openModals.length >= 1;
        },
        topModal(state) {
            return this.hasModals ? state.openModals[0] : null;
        },
    },
    actions: {
        // Passing soft = true will cause the state to be cleared without force deleting all entities.
        // Useful for clearing the state when switching company, where we still wish to keep some
        // entities in our state.
        clearState(soft) {

            soft = soft || false;

            useApplicationsStore().resetState();
            useDashboardStore().resetState();
            useInterviewStore().resetState();
            useJobsStore().resetState();
            useUsersStore().resetState();

            if (!soft) {
                const exceptionModels = ['Feature'];
                Object.entries(piniaModels.default).forEach(([key, value]) => {
                    if (!exceptionModels.includes(key)) {
                        useRepo(value).flush();
                    }
                });
            }
        },
        resetState() {
            Object.assign(this, initialState());
        },
        registerModal(modal) {
            this.openModals.unshift(modal);
        },
        unregisterModal(modal) {
            this.openModals.splice(this.openModals.indexOf(modal), 1);
        },
        setLocale(locale) {
            this.locale = locale;
            this.selectedLocale = locale;
            this.commonStore.setLocale(locale);
            localStorage.setItem('locale', locale);
        },
        confirmLeaving(next) {
            this.confirmLeave = next;
        },
        disableLeaving() {
            this.confirmLeave = false;
        },
        setScrollbarWidth(width) {
            this.scrollbarWidth = width;
        },
        setSelectedLocale(locale) {
            this.selectedLocale = locale;
        },
        resetSelectedLocale() {
            this.selectedLocale = this.locale;
        },
        startAddingTask(context = null) {
            this.tasks.addingTask = true;
            this.tasks.addingContext = context;
        },
        stopAddingTask() {
            this.tasks.addingTask = false;
            this.tasks.addingContext = null;
        },
        startEditingTask(task) {
            this.tasks.editingTask = task;
        },
        stopEditingTask() {
            this.tasks.editingTask = null;
        },
        confirmDeleteTask(task) {
            this.tasks.deletingTask = task;
        },
        cancelDeleteTask() {
            this.tasks.deletingTask = null;
        },
        setRevision(value) {
            this.revision = value;
        },
        setHubspotUrl(value) {
            this.hubspot_meeting = value;
        },
        setWrongRevision() {
            this.wrongRevision = true;
        },
        setDocumentTitle(value) {
            this.documentTitle = value;
        },
        setNotificationCount(value) {
            this.unreadNotificationCount = value;
        },
        setInterfaceLanguages(languages) {
            this.availableLanguages = languages;
            this.commonStore.setAvailableLanguages(languages);
        },
        setOverrideMenuItems(value) {
            this.overrideMenuItems = value;
        },
        setTemplateModuleInfo(value) {
            this.templateModuleInfo = value;
        }
    }
});

function initialState() {
    return {
        openModals: [],
        locale: null,
        confirmLeave: false,
        scrollbarWidth: 0,
        selectedLocale: null,
        availableLanguages: JSON.parse(localStorage.getItem('interfaceLanguages')),
        tasks: {
            addingTask: false,
            addingContext: null,
            editingTask: null,
            deletingTask: null,
        },
        revision: 0,
        wrongRevision: false,
        hubspot_meeting: '',
        documentTitle: null,
        unreadNotificationCount: 0,
        overrideMenuItems: null,
        templateModuleInfo: {
            templateModel: null,
            model: null,
            type: null,
            children: null,
            autocomplete: false,
            hasPivot: true,
            pivot_id: null,
            title: {
                attribute: null,
                locale: null
            },
            text: {
                sub_title: null,
                new_item: null,
                item_short: null,
                no_items_found: null
            },
        },
    };
}
